import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-sonner-toast';
import { useAuth } from '@/contexts/AuthContext';
import { reportIssue } from '@/lib/firebase/reporting';
import { Loader2 } from 'lucide-react';

interface ReportIssueModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ReportIssueModal({ isOpen, onClose }: ReportIssueModalProps) {
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const { currentUser } = useAuth();

  const handleSubmit = async () => {
    if (!description.trim()) {
      toast({
        title: 'Description required',
        description: 'Please provide a description of the issue.',
        variant: 'destructive',
      });
      return;
    }

    if (!currentUser) {
      toast({
        title: 'Sign in required',
        description: 'You need to be signed in to report issues.',
        variant: 'destructive',
      });
      onClose(); // Close the modal if user is not signed in
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await reportIssue(
        currentUser.uid,
        currentUser.email || 'unknown',
        description
      );

      if (result.success) {
        // Show success message, but include warning about email if applicable
        if (result.emailSent) {
          toast({
            title: 'Issue Reported',
            description: "Thank you for your feedback. We'll look into this issue.",
          });
        } else {
          toast({
            title: 'Issue Reported',
            description:
              "Your report was saved, but we couldn't send an email notification. Our team will still review your report.",
            variant: 'warning',
          });
        }

        // Log any warnings for debugging
        if (result.warnings.length > 0) {
          console.warn('Report submitted with warnings:', result.warnings);
        }

        setDescription('');
        onClose();
      } else {
        // This shouldn't happen with the current implementation, but handle it just in case
        toast({
          title: 'Error',
          description: 'Failed to submit report. Please try again later.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error reporting issue:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit report. Please try again later.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Report an Issue</DialogTitle>
          <DialogDescription>
            Describe the issue you're experiencing with VibeCraft. We appreciate your feedback!
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <Textarea
            placeholder="Please describe the issue in detail..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="min-h-[100px]"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Report'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
